import React, { useEffect, useState, useMemo } from 'react';
import { fetchEditCost, updateCostRate, deleteCostRate, fetchCostCategories, fetchNewCostQuantityTypes, fetchRegions } from '../services/api';
import { useRegion } from '../context/RegionContext';
import './PageLayout.css';

function EditCostPage() {
    const { selectedRegion } = useRegion();
    
    // ✅ Initialize as empty arrays to prevent .map errors
    const [materials, setMaterials] = useState([]);
    const [filters, setFilters] = useState({});
    const [categories, setCategories] = useState([]);
    const [quantityTypes, setQuantityTypes] = useState([]);
    const [availableRegions, setAvailableRegions] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    
    // ✅ Add loading states
    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingQuantityTypes, setLoadingQuantityTypes] = useState(false);

    const [editForm, setEditForm] = useState({
        MeasurementUnitRateId: '',
        MeasurementUnitName: '',
        Value: '',
        SKU: '',
        Vendor: '',
        Category: '',
        QuantityTypeName: ''
    });

    // ✅ Load available regions
    useEffect(() => {
        (async () => {
            try {
                const regions = await fetchRegions();
                console.log('🔍 EDIT COST: Fetched regions:', regions);
                setAvailableRegions(Array.isArray(regions) ? regions : []);
            } catch (e) {
                console.error('Error loading regions:', e);
                setAvailableRegions([]);
            }
        })();
    }, []);

    // ✅ Get region name for display
    const regionName = useMemo(() => {
        if (!Array.isArray(availableRegions) || availableRegions.length === 0) {
            return selectedRegion;
        }
        const r = availableRegions.find((r) => r.id === selectedRegion);
        return r ? r.name : selectedRegion;
    }, [availableRegions, selectedRegion]);

    // ✅ Load data when region changes
    useEffect(() => {
        if (!selectedRegion) {
            console.log('🔍 EDIT COST: No selectedRegion yet');
            return;
        }

        const loadCategories = async () => {
            setLoadingCategories(true);
            try {
                console.log('🔍 EDIT COST: Loading categories for region:', selectedRegion);
                const categoriesData = await fetchCostCategories(selectedRegion);
                console.log("📌 Categories for region", selectedRegion, ":", categoriesData);
                
                if (Array.isArray(categoriesData)) {
                    setCategories(categoriesData);
                } else if (categoriesData && Array.isArray(categoriesData.data)) {
                    setCategories(categoriesData.data);
                } else {
                    console.warn('Categories data is not an array:', categoriesData);
                    setCategories([]);
                }
            } catch (error) {
                console.error('❌ Error fetching categories:', error);
                setCategories([]);
            } finally {
                setLoadingCategories(false);
            }
        };

        const loadQuantityTypes = async () => {
            setLoadingQuantityTypes(true);
            try {
                console.log('🔍 EDIT COST: Loading quantity types for region:', selectedRegion);
                const quantityTypesData = await fetchNewCostQuantityTypes(selectedRegion);
                console.log("📌 Quantity Types for region", selectedRegion, ":", quantityTypesData);
                
                if (Array.isArray(quantityTypesData)) {
                    setQuantityTypes(quantityTypesData);
                } else if (quantityTypesData && Array.isArray(quantityTypesData.data)) {
                    setQuantityTypes(quantityTypesData.data);
                } else {
                    console.warn('Quantity types data is not an array:', quantityTypesData);
                    setQuantityTypes([]);
                }
            } catch (error) {
                console.error('❌ Error fetching quantity types:', error);
                setQuantityTypes([]);
            } finally {
                setLoadingQuantityTypes(false);
            }
        };

        const loadCosts = async () => {
            setLoading(true);
            try {
                console.log('🔍 EDIT COST: Loading costs for region:', selectedRegion);
                const response = await fetchEditCost(selectedRegion);
                console.log("📌 Costs for region", selectedRegion, ":", response);
                
                const data = response?.data || response;
                
                // ✅ DEBUG: Check the raw data structure
                console.log("📊 RAW DATA SAMPLE (first 3 items):", data.slice(0, 3));
                console.log("📊 FIRST ITEM ID ANALYSIS:", {
                    MeasurementUnitRateId: data[0]?.MeasurementUnitRateId,
                    MeasurementId: data[0]?.MeasurementId,
                    IdType: typeof data[0]?.MeasurementUnitRateId,
                    IdLength: data[0]?.MeasurementUnitRateId?.length,
                    containsVquest: data[0]?.MeasurementUnitRateId?.includes('vquest')
                });
                
                if (Array.isArray(data)) {
                    setMaterials(data);
                } else {
                    console.warn('Cost data is not an array:', data);
                    setMaterials([]);
                }
            } catch (error) {
                console.error('❌ Error fetching cost data:', error);
                setMaterials([]);
            } finally {
                setLoading(false);
            }
        };


        loadCategories();
        loadQuantityTypes();
        loadCosts();
    }, [selectedRegion]);

    const loadCosts = async () => {
        if (!selectedRegion) return;
        
        setLoading(true);
        try {
            const response = await fetchEditCost(selectedRegion);
            if (response && Array.isArray(response.data)) {
                setMaterials(response.data);
            } else if (Array.isArray(response)) {
                setMaterials(response);
            } else {
                setMaterials([]);
            }
        } catch (error) {
            console.error('Error fetching cost data:', error);
            setMaterials([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value === 'All' ? undefined : value,
        });
    };

    const handleEdit = (id) => {
        if (!id) {
            console.error("❌ Invalid ID for editing:", id);
            return;
        }

        console.log("🔍 Editing item with ID:", id);

        const selectedMaterial = materials.find(mat => mat.MeasurementUnitRateId === id);
        if (!selectedMaterial) {
            console.error("❌ Material not found for ID:", id);
            return;
        }

        console.log("✅ Selected Material:", selectedMaterial);

        const matchingCategory = categories.find(cat => cat.Categories === selectedMaterial.Category);
        const matchingQuantityType = quantityTypes.find(qt => qt.Name === selectedMaterial.QuantityTypeName);

        setEditIndex(id);
        setEditForm({
            MeasurementUnitRateId: selectedMaterial.MeasurementUnitRateId,
            MeasurementUnitName: selectedMaterial.MeasurementUnitName,
            Value: selectedMaterial.Value,
            SKU: selectedMaterial.SKU,
            Vendor: selectedMaterial.Vendor,
            Category: matchingCategory ? matchingCategory.Id : '',
            QuantityTypeName: matchingQuantityType ? matchingQuantityType.Id : ''
        });

        console.log("✅ Updated Edit Form Data:", editForm);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm({ ...editForm, [name]: value });
    };

    const handleSave = async () => {
        if (!editForm.MeasurementUnitRateId) {
            console.error("❌ No valid ID found for update:", editForm);
            return;
        }

        const updatedData = {
            id: editForm.MeasurementUnitRateId,
            name: editForm.MeasurementUnitName,
            value: editForm.Value,
            sku: editForm.SKU,
            vendor: editForm.Vendor,
            categoryId: editForm.Category, 
            quantityTypeId: editForm.QuantityTypeName 
        };

        console.log("🚀 Sending Update Request for region", selectedRegion, ":", updatedData);

        try {
            const response = await updateCostRate(updatedData, selectedRegion);
            console.log("✅ Update Response:", response);
            setEditIndex(null);
            loadCosts();
            alert("✅ Cost updated successfully!");
        } catch (error) {
            console.error("❌ Error updating cost rate:", error.response?.data || error);
        }
    };

    const handleDelete = async (id) => {
        // ✅ Debug: Log what we're trying to delete
        console.log("🗑️ ATTEMPTING TO DELETE ID:", id);
        console.log("🗑️ ID TYPE:", typeof id);
        console.log("🗑️ ID LENGTH:", id?.length);
        
        if (window.confirm("Are you sure you want to delete this cost?")) {
            try {
                // ✅ Make sure we're only passing the UUID, not concatenated with MeasurementId
                const cleanId = id; // We'll verify this is clean
                console.log("🗑️ CLEAN ID BEING SENT:", cleanId);
                
                await deleteCostRate(cleanId);
                await loadCosts(); // ✅ Make sure this reloads properly
                alert("✅ Cost deleted successfully!");
            } catch (error) {
                console.error("❌ Error deleting cost rate:", error);
                alert(`❌ Failed to delete: ${error.response?.data?.message || error.message}`);
            }
        }
    };


    // ✅ Defensive filtering with array checks
    const filteredMaterials = Array.isArray(materials) ? materials.filter((material) => {
        return (
            (filters.MeasurementUnitRateId === undefined || material.MeasurementUnitRateId?.toString().includes(filters.MeasurementUnitRateId)) &&
            (filters.MeasurementUnitName === undefined || material.MeasurementUnitName?.toLowerCase().includes(filters.MeasurementUnitName?.toLowerCase() || '')) &&
            (filters.Category === undefined || material.Category === filters.Category) &&
            (filters.SKU === undefined || material.SKU === filters.SKU) &&
            (filters.QuantityTypeName === undefined || material.QuantityTypeName?.toLowerCase().includes(filters.QuantityTypeName?.toLowerCase() || ''))
        );
    }) : [];

    // ✅ Add debug logging
    console.log('🔍 EDIT COST RENDER STATE:', {
        selectedRegion,
        materialsCount: materials?.length || 0,
        categoriesCount: categories?.length || 0,
        quantityTypesCount: quantityTypes?.length || 0,
        loading
    });

    

    return (
        <div>
            {/* ✅ Add page title section with region indicator */}
            <div className="page-title-section">
                <h1>Edit Cost Data</h1>
                <div className="region-indicator-under-title">
                    🌏 Current Region: <strong>{regionName}</strong>
                </div>
            </div>

            <div className="supply-rates-container">
                <div className="filter-section">
                    <h3>Filters</h3>
                    
                    <div className="filter-group">
                        <label htmlFor="MeasurementUnitRateId">ID</label>
                        <input
                            type="text"
                            name="MeasurementUnitRateId"
                            value={filters.MeasurementUnitRateId || ''}
                            onChange={handleFilterChange}
                            placeholder="Filter by ID"
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="MeasurementUnitName">Name</label>
                        <input
                            type="text"
                            name="MeasurementUnitName"
                            value={filters.MeasurementUnitName || ''}
                            onChange={handleFilterChange}
                            placeholder="Filter by Name"
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="Category">Category</label>
                        <select
                            name="Category"
                            value={filters.Category || 'All'}
                            onChange={handleFilterChange}
                        >
                            <option value="All">All</option>
                            {loadingCategories ? (
                                <option disabled>Loading categories...</option>
                            ) : (
                                Array.isArray(categories) && categories.length > 0 ? (
                                    categories.map((category) => (
                                        <option key={category.Id} value={category.Id}>
                                            {category.Categories}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No categories available</option>
                                )
                            )}
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="SKU">SKU</label>
                        <input
                            type="text"
                            name="SKU"
                            value={filters.SKU || ''}
                            onChange={handleFilterChange}
                            placeholder="Filter by SKU"
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="QuantityTypeName">Quantity Type</label>
                        <select
                            name="QuantityTypeName"
                            value={filters.QuantityTypeName || 'All'}
                            onChange={handleFilterChange}
                        >
                            <option value="All">All</option>
                            {loadingQuantityTypes ? (
                                <option disabled>Loading quantity types...</option>
                            ) : (
                                Array.isArray(quantityTypes) && quantityTypes.length > 0 ? (
                                    quantityTypes.map((qt) => (
                                        <option key={qt.Id} value={qt.Id}>
                                            {qt.Name}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No quantity types available</option>
                                )
                            )}
                        </select>
                    </div>
                </div>

                <div className="table-section">
                    {loading ? (
                        <p>Loading cost data for {regionName}...</p>
                    ) : filteredMaterials.length === 0 ? (
                        <p>No cost data found for {regionName}...</p>
                    ) : (
                        <table className="supply-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Category</th>
                                    <th>Name</th>
                                    <th>Quantity Type</th>
                                    <th>Value</th>
                                    <th>SKU</th>  
                                    <th>Vendor</th>  
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMaterials.map((rate, index) => (
                                    <tr key={rate.MeasurementUnitRateId || index}>
                                        <td>{rate.MeasurementUnitRateId}</td>
                                        <td>
                                            {editIndex === rate.MeasurementUnitRateId ? (
                                                <select name="Category" value={editForm.Category} onChange={handleInputChange}>
                                                    <option value="">Select Category</option>
                                                    {Array.isArray(categories) && categories.map((cat) => (
                                                        <option key={cat.Id} value={cat.Id}>{cat.Categories}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                rate.Category
                                            )}
                                        </td>
                                        <td>
                                            {editIndex === rate.MeasurementUnitRateId ? (
                                                <input type="text" name="MeasurementUnitName" value={editForm.MeasurementUnitName} onChange={handleInputChange} />
                                            ) : (
                                                rate.MeasurementUnitName
                                            )}
                                        </td>
                                        <td>
                                            {editIndex === rate.MeasurementUnitRateId ? (
                                                <select name="QuantityTypeName" value={editForm.QuantityTypeName} onChange={handleInputChange}>
                                                    <option value="">Select Quantity Type</option>
                                                    {Array.isArray(quantityTypes) && quantityTypes.map(qt => (
                                                        <option key={qt.Id} value={qt.Id}>{qt.Name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                rate.QuantityTypeName
                                            )}
                                        </td>
                                        <td>
                                            {editIndex === rate.MeasurementUnitRateId ? (
                                                <input type="number" name="Value" value={editForm.Value} onChange={handleInputChange} />
                                            ) : (
                                                rate.Value
                                            )}
                                        </td>
                                        <td>
                                            {editIndex === rate.MeasurementUnitRateId ? (
                                                <input type="text" name="SKU" value={editForm.SKU} onChange={handleInputChange} />
                                            ) : (
                                                rate.SKU
                                            )}
                                        </td>
                                        <td>
                                            {editIndex === rate.MeasurementUnitRateId ? (
                                                <input type="text" name="Vendor" value={editForm.Vendor} onChange={handleInputChange} />
                                            ) : (
                                                rate.Vendor
                                            )}
                                        </td>
                                        <td>
                                            {editIndex === rate.MeasurementUnitRateId ? (
                                                <button onClick={handleSave}>Save</button>
                                            ) : (
                                                <button onClick={() => handleEdit(rate.MeasurementUnitRateId)}>Edit</button>
                                            )}
                                                <button onClick={() => handleDelete(rate.MeasurementUnitRateId)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default EditCostPage;
